import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Search, Download, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLeads = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [whatsappStats, setWhatsappStats] = useState({ total_clicks: 0, recent_clicks: [] });
  const leadsPerPage = 10;

  useEffect(() => {
    fetchLeads();
    fetchWhatsAppStats();
  }, []);

  useEffect(() => {
    filterLeads();
  }, [leads, searchTerm, filterStatus]);

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/leads`, {
        headers: {
          ...getAuthHeader()
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLeads(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWhatsAppStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/whatsapp-stats`, {
        headers: {
          ...getAuthHeader()
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWhatsappStats(data.data || { total_clicks: 0, recent_clicks: [] });
      }
    } catch (error) {
      console.error('Error fetching WhatsApp stats:', error);
    }
  };

  const filterLeads = () => {
    let filtered = [...leads];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(lead => 
        lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.phone.includes(searchTerm)
      );
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(lead => lead.status === filterStatus);
    }

    setFilteredLeads(filtered);
    setCurrentPage(1);
  };

  const exportToCSV = () => {
    const headers = ['Имя', 'Телефон', 'Email', 'Язык', 'Сообщение', 'UTM Source', 'UTM Medium', 'UTM Campaign', 'Дата', 'Статус'];
    const rows = filteredLeads.map(lead => [
      lead.name,
      lead.phone,
      lead.email,
      lead.language || '-',
      lead.message || '-',
      lead.utm_source || '-',
      lead.utm_medium || '-',
      lead.utm_campaign || '-',
      new Date(lead.created_at).toLocaleString('ru-RU'),
      lead.status
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Pagination
  const indexOfLastLead = currentPage * leadsPerPage;
  const indexOfFirstLead = indexOfLastLead - leadsPerPage;
  const currentLeads = filteredLeads.slice(indexOfFirstLead, indexOfLastLead);
  const totalPages = Math.ceil(filteredLeads.length / leadsPerPage);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatLanguage = (lang) => {
    const languages = {
      'ru': 'Rus',
      'en': 'Eng',
      'es': 'Esp'
    };
    return languages[lang] || lang || '-';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#0052CC] border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Загрузка лидов...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="ghost"
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                Назад
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">Лиды</h1>
            </div>
            <Button
              onClick={exportToCSV}
              className="bg-[#0052CC] hover:bg-[#003366] flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Экспорт CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Поиск по имени, email или телефону..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={filterStatus === 'all' ? 'default' : 'outline'}
                  onClick={() => setFilterStatus('all')}
                  className={filterStatus === 'all' ? 'bg-[#0052CC]' : ''}
                >
                  Все
                </Button>
                <Button
                  variant={filterStatus === 'new' ? 'default' : 'outline'}
                  onClick={() => setFilterStatus('new')}
                  className={filterStatus === 'new' ? 'bg-[#0052CC]' : ''}
                >
                  Новые
                </Button>
                <Button
                  variant={filterStatus === 'contacted' ? 'default' : 'outline'}
                  onClick={() => setFilterStatus('contacted')}
                  className={filterStatus === 'contacted' ? 'bg-[#0052CC]' : ''}
                >
                  Обработано
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Всего лидов</p>
              <p className="text-3xl font-bold text-[#0052CC]">{leads.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Новые</p>
              <p className="text-3xl font-bold text-green-600">
                {leads.filter(l => l.status === 'new').length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Обработано</p>
              <p className="text-3xl font-bold text-gray-600">
                {leads.filter(l => l.status === 'contacted').length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Клики WhatsApp</p>
              <p className="text-3xl font-bold text-[#25D366]">
                {whatsappStats.total_clicks}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Leads Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Имя
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Телефон
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Язык
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Сообщение
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      UTM метки
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Дата
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Статус
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentLeads.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                        Лиды не найдены
                      </td>
                    </tr>
                  ) : (
                    currentLeads.map((lead) => (
                      <tr key={lead.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <a href={`tel:${lead.phone}`} className="text-sm text-[#0052CC] hover:underline">
                            {lead.phone}
                          </a>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <a href={`mailto:${lead.email}`} className="text-sm text-[#0052CC] hover:underline">
                            {lead.email}
                          </a>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatLanguage(lead.language)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">
                            {lead.message || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-xs text-gray-600">
                            {lead.utm_source || lead.utm_medium || lead.utm_campaign ? (
                              <div className="space-y-1">
                                {lead.utm_source && <div><span className="font-semibold">Source:</span> {lead.utm_source}</div>}
                                {lead.utm_medium && <div><span className="font-semibold">Medium:</span> {lead.utm_medium}</div>}
                                {lead.utm_campaign && <div><span className="font-semibold">Campaign:</span> {lead.utm_campaign}</div>}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{formatDate(lead.created_at)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            lead.status === 'new' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {lead.status === 'new' ? 'Новый' : 'Обработан'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Показано {indexOfFirstLead + 1}-{Math.min(indexOfLastLead, filteredLeads.length)} из {filteredLeads.length}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Назад
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Вперед
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLeads;
